import React, { useState, useEffect } from "react";
import { SxProps, Theme } from "@mui/system";
import { Autocomplete, TextField, CircularProgress, InputAdornment } from "@mui/material";
import InfoButton from "./Tooltip/InfoTextField";
import { Option } from "../Types/CommonTypes";
import { useAuth } from "../Context/AuthContext";
import { useLoading } from "../Context/LoadingContext";
interface AsyncAutocompleteProps {
    label: string;
    url: string; // API endpoint
    onChange: (value: Option | null) => void;
    valueId?: string | null; // Accepts ID instead of full Option
    initialOptions?: Option[];
    disabled?: boolean;
    multiple?: boolean;
    load?: boolean; // Auto-load options on mount
    selectFirst?: boolean; // selectFirst
    infoText?: string; // info text 
    error?: boolean;  // error prop
    helperText?: string; //helperText prop
    sx?: SxProps<Theme>;
    onKeyDown?: (e: React.KeyboardEvent) => void
    inputValueOverride?: string; //override Input value
}

const AsyncAutocompleteContained: React.FC<AsyncAutocompleteProps> = ({
    label,
    url,
    onChange,
    valueId,
    initialOptions = [],
    disabled = false,
    multiple = false,
    load = false,
    selectFirst = false,
    infoText,
    error,
    helperText,
    sx,
    onKeyDown,
    inputValueOverride = ""
}) => {
    const { authService } = useAuth();
    const [options, setOptions] = useState<Option[]>(initialOptions);
    const [loading, setLoading] = useState(false);
    const [hasLoadedIntialOptions, setHasLoadedIntialOptions] = useState(false);
    const [inputValue, setInputValue] = useState(inputValueOverride);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [selectedValue, setSelectedValue] = useState<Option | null>(null);
    const [highlighted, setHighlighted] = useState<Option | null>(null);

    // Fetch options from API
    const fetchOptions = async (search: string, offset: number, code?: string | null): Promise<Option[]> => {
        try {
     
            const requestUrl = code
                ? `${url}?code=${code}&offset=${offset}&limit=50`
                : `${url}?search=${search}&offset=${offset}&limit=50`;
            const response = await authService.get<Option[]>(requestUrl);
            return response || [];
        } catch (error) {
            console.error("Error fetching options:", error);
            return [];
        }
    };



    // Load initial options or find selected value
    useEffect(() => {
        const loadInitialData = async () => {
            if (hasLoadedIntialOptions) return;
            setLoading(true);
            try {
                let fetchedOptions: Option[] = load
                    ? await fetchOptions("", 0, valueId)
                    : initialOptions;

         
                let selected: Option | null = null;
                if (valueId) {
                    selected = fetchedOptions.find(opt => opt.value === valueId) || null;
                } else if (selectFirst && fetchedOptions.length > 0) {
                    selected = fetchedOptions[0];
                    onChange(selected);
                }
                setOptions(fetchedOptions);
                setSelectedValue(selected);
                setOffset(fetchedOptions.length);
                setHasMore(fetchedOptions.length > 0);
            } catch (error) {
                console.error("Error loading initial options:", error);
            } finally {
                setLoading(false);
                setHasLoadedIntialOptions(true);
            }
        };

        loadInitialData();
    }, [url, valueId, load, hasLoadedIntialOptions]);



    // Handle user input and fetch matching options
    useEffect(() => {
        const loadFilteredOptions = async () => {
            if (!hasLoadedIntialOptions) return;
            //if (!inputValue.trim()) return;
            if (inputValue === selectedValue?.description) return;
            setLoading(true);
            try {
                const newOptions = await fetchOptions(inputValue, 0);
                setOptions(newOptions);
                setOffset(newOptions.length);
                setHasMore(newOptions.length > 0);
            } catch (error) {
                console.error("Error fetching options:", error);
            } finally {
                setLoading(false);
            }
        };

        loadFilteredOptions();
    }, [inputValue]);

    // Handle scrolling for infinite scrolling
    const handleScroll = async (event: React.SyntheticEvent) => {
        const listboxNode = event.currentTarget;
        if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight && hasMore && !loading) {
            setLoading(true);
            try {
                const newOptions = await fetchOptions(inputValue, offset);
                setOptions((prev) => [...prev, ...newOptions]);
                setOffset((prev) => prev + newOptions.length);
                setHasMore(newOptions.length > 0);
            } catch (error) {
                console.error("Error fetching more options:", error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleKeyDownInternal = (e: React.KeyboardEvent) => {
        // If user hits Enter/Tab and we have options, pick the first one
        if ((e.key === "Tab") && options.length > 0) {
            if (highlighted) {
                e.preventDefault(); // keep focus here long enough to select
                e.stopPropagation();
                setSelectedValue(highlighted);
                onChange(highlighted);
            }
        }

        onKeyDown?.(e);
    };


    return (
        <>
        <Autocomplete
            options={options}
            getOptionLabel={(option) => option.description || ""}
            value={selectedValue}
            onChange={(_, newValue) => {
                setSelectedValue(newValue as Option | null);
                onChange(newValue as Option | null);
                }}
             clearOnEscape
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            loading={loading}
            clearIcon={false}
             autoHighlight
                onKeyDown={handleKeyDownInternal}
                onHighlightChange={(_, option) => setHighlighted(option as Option | null)}
            disabled={disabled}
            multiple={multiple}
            sx={{ width: "100%" }}
            ListboxProps={{
                onScroll: handleScroll,
                style: { maxHeight: 300, overflow: "auto" },
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    error={error}
                    sx={{ ...sx }}
                    helperText={helperText} 
                    InputLabelProps={{ shrink: true }}
       
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                        
                          
                                {params.InputProps.endAdornment}
                                {infoText && (
                                    <InputAdornment position="end">
                                        <InfoButton infoText={infoText} />
                                    </InputAdornment>
                                )}
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                            </>
                        ),
                        autoComplete: 'new-password', // disable autocomplete and autofill
                    }}
                />
            )}
            />
            
        </>
    );
};

export default AsyncAutocompleteContained;
